import {
  voucherStatusName,
  type VoucherStatusName,
  type VoucherStatusNameInput,
} from '@voll-intelligence/domain';

import { formatDate } from './format_date';

type LabelKey = Parameters<(typeof import('@web/i18n'))['t']>[0];

export type TimelineState = 'done' | 'cur' | 'todo' | 'cancelled';

export interface TimelineStep {
  state: TimelineState;
  labelKey: LabelKey;
  /** A source timestamp for the step (already formatted), when one exists. */
  at: string | null;
}

interface TimelineInput extends VoucherStatusNameInput {
  createdAt: Date | null;
  emittedAt?: Date | null | undefined;
}

/** The legacy names that close a voucher without issuing it. */
const ENDED: ReadonlySet<VoucherStatusName> = new Set([
  'CANCELED',
  'REPROVED',
  'REQUEST_DENIED',
  'EXPIRED',
]);

function named(
  state: TimelineState,
  name: VoucherStatusName,
  at: string | null = null,
): TimelineStep {
  return { state, labelKey: `trips.voucherStatus.${name}`, at };
}

/**
 * Derives a coarse status timeline for a voucher from the fields the wire
 * actually carries (`createdAt` → `emittedAt` → the legacy status name).
 * `VoucherView` has no audited event trail, so this is intentionally a
 * status-machine projection, not a rich history — see the design doc's
 * "Limitação conhecida". States map to the drawer's dot styling: `done`
 * (green ✓), `cur` (aubergine, current), `todo` (empty), `cancelled` (red ×).
 */
export function voucherTimeline(input: TimelineInput): TimelineStep[] {
  const name = voucherStatusName(input);
  const created: TimelineStep = {
    state: input.createdAt ? 'done' : 'todo',
    labelKey: 'customers.voucher.timeline.created',
    at: input.createdAt ? formatDate(input.createdAt) : null,
  };
  const emittedAt = input.emittedAt ? formatDate(input.emittedAt) : null;

  if (name && ENDED.has(name)) return [created, named('cancelled', name)];
  if (name === 'EMITTED') return [created, named('done', 'EMITTED', emittedAt)];
  if (name === 'CANCELLATION_REQUESTED' || name === 'CHANGE_REQUESTED') {
    return [created, named('done', 'EMITTED', emittedAt), named('cur', name)];
  }
  return name
    ? [created, named('cur', name), named('todo', 'EMITTED')]
    : [created, named('todo', 'EMITTED')];
}
